import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * useDebounce hook - debounce a value with configurable delay
 * @param {any} value - Value to debounce
 * @param {number} delay - Delay in milliseconds (default: 300ms)
 * @returns {any} Debounced value
 */
export function useDebounce(value, delay = 300) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * useDebounceCallback hook - debounce a callback function
 * @param {Function} callback - Function to debounce
 * @param {number} delay - Delay in milliseconds
 * @returns {Function} Debounced function
 */
export function useDebounceCallback(callback, delay = 300) {
  const timeoutRef = useRef(null);

  const debouncedCallback = useCallback((...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  }, [callback, delay]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return debouncedCallback;
}

/**
 * Simple in-memory cache for reference data
 */
const cache = new Map();
const cacheExpiry = new Map();

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * useCachedData hook - cache API responses for stable reference data
 * @param {string} key - Cache key
 * @param {Function} fetchFn - Async function to fetch data
 * @param {Object} options - Options { enabled: boolean, staleTime: number }
 * @returns {{ data, loading, error, refetch }}
 */
export function useCachedData(key, fetchFn, options = {}) {
  const { enabled = true, staleTime = CACHE_DURATION } = options;
  const [data, setData] = useState(() => cache.get(key) || null);
  const [loading, setLoading] = useState(!cache.has(key));
  const [error, setError] = useState(null);

  const fetchData = useCallback(async (force = false) => {
    // Check cache first
    const cachedData = cache.get(key);
    const expiry = cacheExpiry.get(key);
    
    if (!force && cachedData && expiry && Date.now() < expiry) {
      setData(cachedData);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const result = await fetchFn();
      cache.set(key, result);
      cacheExpiry.set(key, Date.now() + staleTime);
      setData(result);
      setError(null);
    } catch (err) {
      setError(err);
      // Keep stale data if available
      if (cachedData) {
        setData(cachedData);
      }
    } finally {
      setLoading(false);
    }
  }, [key, fetchFn, staleTime]);

  useEffect(() => {
    if (enabled) {
      fetchData();
    }
  }, [enabled, fetchData]);

  const refetch = useCallback(() => fetchData(true), [fetchData]);

  return { data, loading, error, refetch };
}

/**
 * Clear all cached data
 */
export function clearCache() {
  cache.clear();
  cacheExpiry.clear();
}

/**
 * Clear specific cache key
 */
export function invalidateCache(key) {
  cache.delete(key);
  cacheExpiry.delete(key);
}

export default { useDebounce, useDebounceCallback, useCachedData, clearCache, invalidateCache };
